const HOUR_LABELS = [0, 6, 12, 18];

const heatColor = (v: number) => {
  if (v >= 0.75) return "bg-[#EF4444]";
  if (v >= 0.5) return "bg-[#F97316]";
  if (v >= 0.25) return "bg-[#EAB308]";
  if (v > 0.05) return "bg-[#22C55E]";
  return "bg-[#1E3A5F]";
};

const occColor = (v: number) => {
  if (v >= 0.99) return "bg-red-500/80";
  if (v >= 0.66) return "bg-red-500/45";
  if (v >= 0.33) return "bg-amber-500/50";
  if (v > 0) return "bg-emerald-500/40";
  return "bg-emerald-500/15";
};

export const SlotHeatmap = ({ availMap, maxSlots, windowDays, startDate, traffic, hourRange, onPickHour, primeHours, primePct }: {
  availMap: Record<string, any>;
  maxSlots: number;
  windowDays: number;
  startDate?: Date;
  traffic?: number[];
  hourRange?: { start: number; len: number };
  onPickHour?: (h: number) => void;
  primeHours?: number[];
  primePct?: number;
}) => {
  const start = startDate ? new Date(startDate) : new Date();
  const occupancy: number[] = Array.from({ length: 24 }, () => 0);
  let counted = 0;
  for (let i = 0; i < windowDays; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const info = availMap[key];
    if (!info?.hours) continue;
    counted++;
    for (let h = 0; h < 24; h++)
      occupancy[h] += info.excl?.[h] ? 1 : Math.min((info.hours[h] || 0) / maxSlots, 1);
  }
  if (counted > 0) for (let h = 0; h < 24; h++) occupancy[h] /= counted;
  const maxTraffic = Math.max(1, ...(traffic || []));
  const inRange = (h: number) => hourRange && h >= hourRange.start && h < hourRange.start + hourRange.len;
  const pickable = (h: number) => onPickHour && hourRange && hourRange.len < 24 && h + hourRange.len <= 24;

  return (
    <div className="mt-4 bg-[#101010] border border-white/10 rounded-lg p-4" data-testid="slot-heatmap">
      <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">
        Hour-by-hour (ET) — spot the prime slots before you book
      </p>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[9px] text-[#F97316] w-20 shrink-0 text-right">Reader traffic</span>
          <div className="flex gap-[2px] flex-1">
            {Array.from({ length: 24 }, (_, h) => {
              const v = (traffic?.[h] || 0) / maxTraffic;
              return (
                <div key={h}
                  title={`${String(h).padStart(2, "0")}:00 ET — ${traffic?.[h] ?? 0} reads this week${primeHours?.includes(h) ? ` · prime (+${primePct ?? 25}%)` : ""}`}
                  className={`flex-1 h-4 rounded-[2px] ${heatColor(v)} ${primeHours?.includes(h) ? "ring-1 ring-white/90" : ""}`} />
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[9px] text-gray-400 w-20 shrink-0 text-right">Slot demand</span>
          <div className="flex gap-[2px] flex-1">
            {Array.from({ length: 24 }, (_, h) => (
              <button key={h} type="button" data-testid={`heatmap-hour-${h}`}
                onClick={() => pickable(h) && onPickHour!(h)}
                title={`${String(h).padStart(2, "0")}:00 ET — ${Math.round(occupancy[h] * 100)}% booked in your window${pickable(h) ? " · click to start here" : ""}`}
                className={`flex-1 h-4 rounded-[2px] transition-transform ${occColor(occupancy[h])} ${
                  inRange(h) ? "ring-1 ring-[#00E5FF] ring-offset-1 ring-offset-[#101010]" : ""} ${
                  pickable(h) ? "cursor-pointer hover:scale-y-125" : "cursor-default"}`} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0" />
          <div className="flex flex-1 justify-between">
            {HOUR_LABELS.map((h) => (
              <span key={h} className="font-mono text-[9px] text-gray-600">{String(h).padStart(2, "0")}:00</span>
            ))}
            <span className="font-mono text-[9px] text-gray-600">24:00</span>
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-1.5" data-testid="heatmap-legend">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500 w-20 shrink-0 text-right">Traffic</span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-[#EF4444]" /> hottest
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-[#F97316]" /> hot
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-[#EAB308]" /> warm
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-[#22C55E]" /> mild
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-[#1E3A5F]" /> quiet
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-[#EF4444] ring-1 ring-white/90" /> prime time (+{primePct ?? 25}%)
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500 w-20 shrink-0 text-right">Demand</span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-emerald-500/30" /> open
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-amber-500/50" /> filling up
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
            <span className="h-2.5 w-2.5 rounded-[2px] bg-red-500/70" /> booked
          </span>
        </div>
      </div>
    </div>
  );
};
